import { useMemo } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';

import ImageLightbox from '../components/ImageLightbox/ImageLightbox';
import PropertyVisual from '../components/PropertyVisual';
import { useLanguage } from '../context/LanguageContext';
import { getResidence } from '../data/residences';
import { useImageLightbox } from '../hooks/useImageLightbox';

const DETAIL_ICON_NAMES = [
  'guests',
  'area',
  'bedrooms',
  'bathrooms',
  'kitchen',
  'wifi',
  'parking',
];

function ResidenceDetailIcon({ name }) {
  const iconProps = {
    'aria-hidden': true,
    className: 'residence-story__detail-icon',
    fill: 'none',
    viewBox: '0 0 24 24',
  };

  switch (name) {
    case 'guests':
      return (
        <svg {...iconProps}>
          <circle cx="9" cy="8" r="3" />
          <path d="M3.5 19c.5-4 2.25-6 5.5-6s5 2 5.5 6" />
          <circle cx="17" cy="9" r="2.25" />
          <path d="M15.5 14c2.9-.35 4.55 1.3 5 4.5" />
        </svg>
      );
    case 'area':
      return (
        <svg {...iconProps}>
          <path d="M9 4H4v5M15 4h5v5M20 15v5h-5M9 20H4v-5" />
          <path d="m4 9 5-5M15 4l5 5M20 15l-5 5M9 20l-5-5" />
        </svg>
      );
    case 'bedrooms':
      return (
        <svg {...iconProps}>
          <path d="M3 19v-9M21 19v-6H3v6M6 13V8h5a3 3 0 0 1 3 3v2M3 17h18" />
        </svg>
      );
    case 'bathrooms':
      return (
        <svg {...iconProps}>
          <path d="M4 13h16v2a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5v-2ZM7 13V6a3 3 0 0 1 5.5-1.65" />
          <path d="M4 20v1M20 20v1" />
        </svg>
      );
    case 'kitchen':
      return (
        <svg {...iconProps}>
          <path d="M7 3v7M4.5 3v4.5A2.5 2.5 0 0 0 7 10v11M9.5 3v4.5A2.5 2.5 0 0 1 7 10M16 3v18M16 3c2.25 1.5 3.5 4 3.5 7H16" />
        </svg>
      );
    case 'wifi':
      return (
        <svg {...iconProps}>
          <path d="M3 9a14.5 14.5 0 0 1 18 0M6.5 13a9 9 0 0 1 11 0M10 17a3.5 3.5 0 0 1 4 0" />
          <circle cx="12" cy="20" r=".75" fill="currentColor" stroke="none" />
        </svg>
      );
    case 'parking':
      return (
        <svg {...iconProps}>
          <rect x="5" y="3" width="14" height="18" rx="2" />
          <path d="M10 17V7h3a3 3 0 0 1 0 6h-3" />
        </svg>
      );
    default:
      return null;
  }
}

function ResidencePage() {
  const { residenceId } = useParams();
  const [searchParams] = useSearchParams();
  const { language, t } = useLanguage();
  const residence = getResidence(residenceId);
  const returnToBooking = searchParams.get('return') === 'booking';

  const galleryImages = useMemo(() => residence?.gallery.map((src, index) => ({
    id: `${residence.id}-${index + 1}`,
    src,
    alt: language === 'ru'
      ? `${residence.name.ru} — фотография ${index + 1}`
      : `${residence.name.en} — photo ${index + 1}`,
  })) ?? [], [language, residence]);
  const lightbox = useImageLightbox(galleryImages);

  if (!residence) {
    return <Navigate to="/residences" replace />;
  }

  const copy = language === 'ru'
    ? {
        about: 'Об апартаменте',
        amenities: 'В апартаменте',
        gallery: 'Фотографии апартамента',
        galleryLead: 'Интерьер, спальни и пространство для отдыха',
        backToSearch: 'Назад к результатам поиска',
      }
    : {
        about: 'About the apartment',
        amenities: 'Included with your stay',
        gallery: 'Inside the apartment',
        galleryLead: 'Living spaces, bedrooms and room to unwind',
        backToSearch: 'Back to search results',
      };

  const details = [...residence.specs[language], ...residence.amenities[language]];

  return (
    <article className="residence-page">
      <section className="residence-hero">
        <PropertyVisual src={residence.image} alt={residence.name[language]} eager />
        <div className="residence-hero__title">
          <span className="eyebrow eyebrow_light">{residence.category[language]}</span>
          <h1>{residence.name[language]}</h1>
        </div>
      </section>

      <section className="section residence-story">
        <div>
          <Link className="text-link" to={returnToBooking ? '/book#availability' : '/residences'}>
            ← {returnToBooking ? copy.backToSearch : t('common.back')}
          </Link>
        </div>
        <div className="residence-story__copy">
          <span className="eyebrow">{copy.about}</span>
          <p>{residence.description[language]}</p>
          <div className="residence-story__details">
            {details.map((detail, index) => (
              <span key={detail}>
                <ResidenceDetailIcon name={DETAIL_ICON_NAMES[index]} />
                <b>{detail}</b>
              </span>
            ))}
          </div>
          <Link className="button" to={`/book?residence=${residence.id}`}>{t('common.book')}</Link>
        </div>
      </section>

      <section className="section section_wide residence-gallery-section">
        <div className="section-heading section-heading_split">
          <h2>{copy.gallery}</h2>
          <p>{copy.galleryLead}</p>
        </div>
        <div className="residence-gallery">
          {galleryImages.map((image, index) => (
            <button
              className="residence-gallery__item"
              type="button"
              key={image.id}
              onClick={() => lightbox.open(index)}
              aria-label={language === 'ru' ? `Открыть: ${image.alt}` : `Open ${image.alt}`}
            >
              <img
                src={image.src}
                alt={image.alt}
                loading={index < 3 ? 'eager' : 'lazy'}
                decoding="async"
              />
            </button>
          ))}
        </div>
      </section>

      <ImageLightbox
        activeIndex={lightbox.activeIndex}
        images={galleryImages}
        isOpen={lightbox.isOpen}
        onClose={lightbox.close}
      />
    </article>
  );
}

export default ResidencePage;
